import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PriceType } from 'types/api/pricing.type';

import { useAppSelector } from 'redux/hooks';
import { getUserPermissions } from 'redux/slices/userData';

import { Button } from 'components/Button';
import { Modal } from 'components/Modal';

import * as S from './PriceTypeModal.styles';

type PriceTypeOption = PriceType | 'NO_PRICE';

type PriceTypeModalProps = {
  onClose: () => void;
  onConfirm: (selectedType: PriceTypeOption) => void;
  isLoading?: boolean;
  /**
   * Price types the order actually resolves. Only these are offered (plus
   * "No price"); an empty list leaves "No price" alone. Undefined = not known,
   * nothing is filtered.
   */
  availableTypes?: PriceType[];
};

const PriceTypeModal = ({
  onClose,
  onConfirm,
  isLoading,
  availableTypes,
}: PriceTypeModalProps) => {
  const { t } = useTranslation();
  const permissions = useAppSelector(getUserPermissions);
  const canViewDistributorPrice = permissions.includes('VIEW_DISTRIBUTOR_PRICE');

  const options = useMemo(() => {
    const base: { value: PriceTypeOption; label: string }[] = [
      { value: 'NO_PRICE', label: String(t('catalogue.noPrice')) },
    ];

    // Distributor price: only for users with the "View Distributor Price" flag,
    // independent of the "Distributor" flag (which marks a distributor user and
    // has no effect on which price types are shown).
    if (canViewDistributorPrice) {
      base.push({
        value: 'DISTRIBUTOR',
        label: String(t('catalogue.distributorPrice')),
      });
    }

    base.push({
      value: 'WHOLESALE',
      label: String(t('catalogue.wholesalePrice')),
    });

    base.push({
      value: 'RETAIL',
      label: String(t('catalogue.retailPrice')),
    });

    // If we know which types are actually available, filter out unavailable ones
    // (but always keep NO_PRICE)
    if (availableTypes) {
      return base.filter(
        (opt) => opt.value === 'NO_PRICE' || availableTypes.includes(opt.value as PriceType)
      );
    }

    return base;
  }, [canViewDistributorPrice, availableTypes, t]);

  const [chosen, setChosen] = useState<PriceTypeOption>('WHOLESALE');
  // The options can shrink after mount (available types arrive later): an
  // option no longer offered is never shown as selected nor confirmed.
  const selected = options.some((opt) => opt.value === chosen)
    ? chosen
    : options.find((opt) => opt.value !== 'NO_PRICE')?.value ?? 'NO_PRICE';

  return (
    <Modal onClose={onClose}>
      <S.Container>
        <S.Title scale="xl3" color="grayDark">
          {t('catalogue.priceType')}
        </S.Title>

        <S.OptionsWrapper>
          {options.map((opt) => (
            <S.OptionLabel key={opt.value}>
              <S.RadioInput
                type="radio"
                name="priceType"
                value={opt.value}
                checked={selected === opt.value}
                onChange={() => setChosen(opt.value)}
              />
              {opt.label}
            </S.OptionLabel>
          ))}
        </S.OptionsWrapper>

        <S.ButtonWrapper>
          <Button
            onClick={onClose}
            motive="cancelTertiary"
            fontFamily="LFT Zeno"
            textTransform="isFirstLetterUppercase"
          >
            {t('common.cancel')}
          </Button>

          <Button
            textTransform="isFirstLetterUppercase"
            fontWeight="light"
            fontFamily="LFT Zeno"
            motive="actionTertiary"
            onClick={() => onConfirm(selected)}
            isLoading={isLoading}
          >
            {t('common.confirm')}
          </Button>
        </S.ButtonWrapper>
      </S.Container>
    </Modal>
  );
};

export { PriceTypeModal };
export type { PriceTypeOption };
